if (!customElements.get('sistaco-how-to')) {
  customElements.define(
    'sistaco-how-to',
    class SistacoPressOnsHowTo extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'true';

        this._tabs = Array.from(this.querySelectorAll('[data-po-tab]'));
        this._pausedByUser = new WeakSet();

        this._tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this._selectTab(tab.dataset.poTab));
          tab.addEventListener('keydown', (event) => {
            let next;
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = this._tabs[1 - index];
            if (event.key === 'Home') next = this._tabs[0];
            if (event.key === 'End') next = this._tabs[this._tabs.length - 1];
            if (next) {
              event.preventDefault();
              this._selectTab(next.dataset.poTab);
              next.focus();
            }
          });
        });

        this.querySelectorAll('[data-po-step]').forEach((step) => {
          step.addEventListener('click', () => this._selectStep(step));
        });

        this.querySelectorAll('video').forEach((video) => {
          video.addEventListener('pause', () => {
            if (this._inView && !video.closest('[data-po-video]').hidden && !video.ended && !document.hidden) {
              this._pausedByUser.add(video);
            }
          });
          video.addEventListener('play', () => this._pausedByUser.delete(video));
          // Re-attempt playback once the global lazy-loader has finished loading sources.
          // This fires after video.load() completes, covering the case where the video's
          // container was hidden on page load and sources were not yet fetched.
          video.addEventListener('canplay', () => this._updatePlayback());
        });

        this._visibilityHandler = () => this._updatePlayback();
        document.addEventListener('visibilitychange', this._visibilityHandler);

        this._videoObserver = new IntersectionObserver(
          (entries) => {
            this._inView = entries[0].isIntersecting;
            this._updatePlayback();
          },
          { threshold: 0.01 }
        );
        const mediaEl = this.querySelector('.po-howto__media');
        if (mediaEl) this._videoObserver.observe(mediaEl);

        this._initVideoControls();
        this._selectTab('apply');
      }

      disconnectedCallback() {
        this._videoObserver?.disconnect();
        document.removeEventListener('visibilitychange', this._visibilityHandler);
      }

      _initVideoControls() {
        this.querySelectorAll('[data-po-video]').forEach((container) => {
          const video = container.querySelector('video');
          const controls = container.querySelector('.po-howto__controls');
          if (!video || !controls) return;

          video.controls = false;
          controls.hidden = false;

          const muteBtn = controls.querySelector('[data-po-mute]');
          const playBtn = controls.querySelector('[data-po-play]');

          muteBtn?.addEventListener('click', () => {
            const newMuted = !video.muted;
            this.querySelectorAll('[data-po-video] video').forEach((v) => {
              v.muted = newMuted;
            });
          });

          playBtn?.addEventListener('click', () => {
            if (video.paused) video.play().catch(() => {});
            else video.pause();
          });

          ['play', 'pause', 'volumechange', 'ended'].forEach((event) => {
            video.addEventListener(event, () => this._syncControls(video));
          });

          this._syncControls(video);
        });
      }

      _syncControls(video) {
        const container = video.closest('[data-po-video]');
        if (!container) return;

        const muteBtn = container.querySelector('[data-po-mute]');
        const playBtn = container.querySelector('[data-po-play]');

        if (muteBtn) {
          muteBtn.dataset.muted = String(video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        }

        if (playBtn) {
          playBtn.dataset.playing = String(!video.paused);
          playBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        }
      }

      _updatePlayback() {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.querySelectorAll('[data-po-video]').forEach((media) => {
          const video = media.querySelector('video');
          if (!video) return;
          if (media.hidden || !this._inView || document.hidden) {
            video.pause();
          } else if (!reducedMotion && !video.ended && !this._pausedByUser.has(video) && video.paused) {
            video.play().catch(() => {});
          }
        });
      }

      _selectTab(mode) {
        if (this._activeMode === mode) return;
        this._activeMode = mode;

        this.querySelectorAll('[data-po-video]').forEach((media) => {
          media.hidden = media.dataset.poVideo !== mode;
          const video = media.querySelector('video');
          if (video && !media.hidden && video.ended) video.currentTime = 0;
        });

        this._updatePlayback();

        this._tabs.forEach((tab) => {
          const selected = tab.dataset.poTab === mode;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });

        this.querySelectorAll('[data-po-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.poPanel !== mode;
          if (!panel.hidden) this._selectStep(panel.querySelector('[data-po-step]'));
        });
      }

      _selectStep(selected) {
        if (!selected) return;
        const panel = selected.closest('[data-po-panel]');
        panel.querySelectorAll('[data-po-step]').forEach((step) => {
          step.setAttribute('aria-pressed', String(step === selected));
        });

        // Video tabs own the media column — keep step images hidden.
        if (this.querySelector(`[data-po-video="${this._activeMode}"]`)) {
          this.querySelectorAll('[data-po-media]').forEach((media) => {
            media.hidden = true;
          });
          return;
        }

        this.querySelectorAll('[data-po-media]').forEach((media) => {
          media.hidden = media.dataset.poMedia !== selected.dataset.poStep;
        });
      }
    }
  );
}
